import React from 'react';
import { aboutText } from '../data/index';

/**
 * A horizontal, infinitely-scrolling "running line" that displays the
 * `aboutText` from /src/data/index.js. The text is duplicated so the loop is
 * seamless; scroll animation is driven by the `marquee` keyframe defined in
 * /src/index.css. Hovering the strip pauses the animation.
 */
const RunningLineAbout: React.FC = () => {
    // Collapse internal whitespace / newlines so the ticker reads as one line.
    const text = aboutText.replace(/\s+/g, ' ').trim();

    return (
        <section
            aria-label="About Forge Ultra"
            className="group relative w-full overflow-hidden
                       border-y border-slate-200 dark:border-slate-800
                       bg-slate-100/70 dark:bg-slate-900/60 backdrop-blur-sm
                       py-3 sm:py-4"
        >
            {/* Left + right fade masks so the text elegantly fades into the edges */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-0 w-8 sm:w-16
                           bg-gradient-to-r from-slate-100 dark:from-slate-900 to-transparent z-10"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 w-8 sm:w-16
                           bg-gradient-to-l from-slate-100 dark:from-slate-900 to-transparent z-10"
            />

            <div
                className="flex w-max items-center gap-12 sm:gap-16
                           animate-[marquee_45s_linear_infinite]
                           group-hover:[animation-play-state:paused]
                           motion-reduce:animation-none"
            >
                {/* Duplicate the content twice to produce a seamless loop with
                    a -50% translateX animation. `aria-hidden` on the second copy
                    so screen readers only announce the text once. */}
                {[0, 1].map((i) => (
                    <p
                        key={i}
                        aria-hidden={i === 1 ? true : undefined}
                        className="shrink-0 whitespace-nowrap text-sm sm:text-base lg:text-lg
                                   text-slate-600 dark:text-slate-300 italic tracking-wide"
                    >
                        <span className="text-emerald-500 dark:text-emerald-400 font-bold not-italic mr-3">
                            Forge Ultra
                        </span>
                        {text}
                        <span aria-hidden="true" className="mx-6 text-emerald-400/60">
                            &bull;
                        </span>
                    </p>
                ))}
            </div>
        </section>
    );
};

export default RunningLineAbout;
